/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@shared/i18n';
import type { ColumnMapping, ImportPreview, PreviewRow } from '../types';
import { FieldChipSelect } from './FieldChipSelect';
import { PreviewCellContent, STATUS_DOT, headerCellStyle, rowNumberCellStyle } from './preview-table-parts';
import { useRowWindow, ROW_WINDOW_STEP } from '../hooks/use-row-window';
import { Button, Text } from '@/shared/ui';

export interface ImportPreviewTableProps {
  preview: ImportPreview;
  onColumnFieldChange: (index: number, field: string | null) => void;
  /** Replaces one cell's text for the import; `null` restores the sheet's. */
  onCellEdit: (rowNumber: number, columnIndex: number, text: string | null) => void;
  /** Answers "é pessoa jurídica?" for one row's owner cell. */
  onOwnerTypeAnswer: (rowNumber: number, answer: 'individual' | 'company') => void;
  /** True while a commit is in flight: nothing in the table can be touched. */
  disabled?: boolean;
}

/** One column header: the sheet's own name, then the chip that maps it. */
const HeaderCell: React.FC<{
  column: ColumnMapping;
  options: ImportPreview['field_options'];
  onChange: (index: number, field: string | null) => void;
  disabled: boolean;
}> = ({ column, options, onChange, disabled }) => (
  <th scope="col" style={headerCellStyle}>
    <div style={{ fontSize: 12, color: 'var(--text-tertiary-soft)', marginBottom: 6 }}>
      {column.original_name}
    </div>
    <FieldChipSelect
      originalName={column.original_name}
      value={column.field}
      options={options}
      onChange={(field) => onChange(column.index, field)}
      disabled={disabled}
    />
    {column.warning && (
      // Said once here rather than on each of the column's rows: it is true
      // of the column, and 1.261 identical notes would read as 1.261 problems.
      <div
        data-testid="import-column-warning"
        style={{
          marginTop: 4, maxWidth: 220, whiteSpace: 'normal',
          fontSize: 11, color: 'var(--status-warning-text)',
        }}
      >
        {column.warning}
      </div>
    )}
  </th>
);

/** Which earlier line a repeated row copies, shown when hovering its number. */
function repeatTitle(row: PreviewRow): string | undefined {
  if (row.status !== 'repeated' || row.match?.row_number == null) return undefined;
  return i18n.t('features/spreadsheets:preview.repeatOf', { row: row.match.row_number });
}

/** One spreadsheet row, keeping its original line number in the gutter. */
const BodyRow = React.memo<{
  row: PreviewRow;
  columns: ColumnMapping[];
  onCellEdit: ImportPreviewTableProps['onCellEdit'];
  onOwnerTypeAnswer: ImportPreviewTableProps['onOwnerTypeAnswer'];
  disabled: boolean;
}>(({ row, columns, onCellEdit, onOwnerTypeAnswer, disabled }) => (
  <tr data-testid="import-preview-row">
    <td style={rowNumberCellStyle} title={repeatTitle(row)}>
      <Text component="span" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Text component="span" style={{
          width: 7, height: 7, borderRadius: 'var(--radius-full)',
          background: STATUS_DOT[row.status],
        }}  />
        <Text component="span" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11, color: 'var(--text-tertiary-soft)' }}>
          {row.row_number}
        </Text>
      </Text>
    </td>
    {row.cells.map((cell, index) => (
      <PreviewCellContent
        key={columns[index]?.index ?? index}
        cell={cell}
        status={row.status}
        ignored={columns[index]?.field == null}
        options={columns[index]?.options}
        disabled={disabled}
        onEdit={columns[index] == null ? undefined : (text) => {
          onCellEdit(row.row_number, columns[index].index, text);
        }}
        onPersonTypeAnswer={columns[index]?.field !== 'owner' ? undefined : (answer) => {
          onOwnerTypeAnswer(row.row_number, answer);
        }}
      />
    ))}
  </tr>
));
BodyRow.displayName = 'BodyRow';

/**
 * The spreadsheet as it actually is, not a Housien listing.
 *
 * Deliberately not a `ListingTable`: the point of this screen is that the
 * user recognises their own file, so the original column names stay, the
 * original row numbers stay, and every cell keeps the text that was typed --
 * with the Housien field it maps to sitting underneath the header.
 *
 * @param props - The preview to render and the chip change handler.
 * @returns The rendered table.
 */
export const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  preview, onColumnFieldChange, onCellEdit, onOwnerTypeAnswer, disabled = false,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const rows = preview.rows;
  const rowWindow = useRowWindow(rows.length, `${preview.counts.total}:${rows[0]?.row_number ?? ''}:${rows.length}`);
  const hidden = rows.length - rowWindow.shown;

  return (
    <div
      data-testid="import-preview-table"
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'auto',
        maxHeight: '100%',
      }}
    >
      {/* Not DataGrid on purpose: this screen has to look like the user's own
          file, with their column names, their row numbers and their text. A
          DS grid would render a Housien listing, which is exactly the thing
          the Conferir step exists to NOT show yet. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: '100%',
          fontSize: 13,
          fontFamily: 'var(--font-family)',
        }}
      >
        <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          {t('preview.showing', { shown: rowWindow.shown, total: preview.counts.total })}
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              style={{
                position: 'sticky', top: 0, zIndex: 2, width: 48,
                background: 'var(--surface-primary)',
                borderBottom: '1px solid var(--border-subtle)',
                borderRight: '1px solid var(--border-subtle)',
              }}
            >
              <Text component="span" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                {preview.counts.total}
              </Text>
            </th>
            {preview.columns.map((column) => (
              <HeaderCell
                key={column.index}
                column={column}
                options={preview.field_options}
                onChange={onColumnFieldChange}
                disabled={disabled}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, rowWindow.shown).map((row) => (
            <BodyRow
              key={row.row_number}
              row={row}
              columns={preview.columns}
              onCellEdit={onCellEdit}
              onOwnerTypeAnswer={onOwnerTypeAnswer}
              disabled={disabled}
            />
          ))}
          {hidden > 0 && (
            <tr ref={rowWindow.sentinelRef} data-testid="import-preview-more">
              <td colSpan={preview.columns.length + 1} style={{ padding: 'var(--space-3)' }}>
                <Button variant="outline" size="sm" onClick={rowWindow.showMore}>
                  {t('preview.showMore', { count: Math.min(hidden, ROW_WINDOW_STEP), remaining: hidden })}
                </Button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
